// 2026-09-24 FASE 2 FIXER L-30 double-owner infra-status (research 2026 SWR
// dedup + free-tier CPU budget, paridad fetchUsageShared en
// use-chat-quotas.ts + OnlineGuard PROBE_DEDUP_MS): OverloadBanner (120s) y
// StatusDashboard (180s) pedían /api/infra-status por separado — 2 owners del
// mismo poll. Este slot compartido por tab (ventana 30s + in-flight) colapsa
// ambos en ≤1 hit/30s/tab. Respeta Cache-Control del server (public
// max-age=60, sin cache:no-store en cliente). Force (refresh manual del
// usuario) bypassea la ventana pero comparte el in-flight.
let sharedLastFetchMs = 0;
let sharedInflightCount = 0;
let sharedInflight: Promise<{
  phase?: string;
  reasons?: string[];
  message?: {
    banner?: string | null;
    bannerTone?: "info" | "warn" | "critical" | null;
    featureDegraded?: string | null;
  };
} | null> | null = null;

/** Test-only: resetea el slot compartido entre tests. */
export function __resetInfraStatusDedupForTests(): void {
  sharedLastFetchMs = 0;
  sharedInflightCount = 0;
  sharedInflight = null;
}

/**
 * Reclama el slot de fetch a /api/infra-status (ventana 30s + in-flight).
 * Devuelve false cuando otro owner pidió hace <30s o hay fetch en curso.
 * 2026-10-03 RUN12 F2 M4: sin `export` — 0 imports externos (verificado por
 * grep); el único consumidor es `fetchInfraStatusShared` abajo. Knip lo
 * listaba como unused export.
 * 2026-10-10 E9: la ventana SOLO avanza en éxito (fetchInfraStatusShared la
 * marca tras res.ok + payload), igual que `tryClaimSharedUsageFetch`
 * (use-chat-quotas.ts:119-128) — antes se marcaba aquí en el CLAIM, así que
 * un 5xx dejaba el OverloadBanner ciego 30 s sin reintento posible.
 */
function tryClaimSharedInfraFetch(force = false): boolean {
  const now = Date.now();
  if (!force && (sharedInflightCount > 0 || now - sharedLastFetchMs < 30_000))
    return false;
  sharedInflightCount += 1;
  return true;
}

/** Libera el slot reclamado (llamar en finally del fetch). */
function releaseSharedInfraFetch(): void {
  sharedInflightCount = Math.max(0, sharedInflightCount - 1);
}

/** Fetch compartido a /api/infra-status (1 hit/30s/tab máx). */
export async function fetchInfraStatusShared(
  init?: RequestInit,
  force = false,
) {
  if (sharedInflight) return sharedInflight;
  if (!tryClaimSharedInfraFetch(force)) return null;
  sharedInflight = (async () => {
    try {
      // Sin cache:no-store: respeta public max-age=60 del server.
      // 2026-09-28 SEED 2026-09-28-bae4824d #25: fusionar headers, no
      // reemplazar — el spread ...init sobrescribía Accept cuando el caller
      // pasaba headers propios (OverloadBanner manda Accept-Language). El
      // default Accept: application/json se conserva salvo override explícito
      // del caller; signal R19 y resto de init intactos.
      const { headers: initHeaders, ...restInit } = init ?? {};
      const mergedHeaders: Record<string, string> = {
        Accept: "application/json",
        ...(initHeaders instanceof Headers
          ? Object.fromEntries(initHeaders.entries())
          : Array.isArray(initHeaders)
            ? Object.fromEntries(initHeaders)
            : ((initHeaders as Record<string, string> | undefined) ?? {})),
      };
      const res = await fetch("/api/infra-status", {
        ...restInit,
        headers: mergedHeaders,
      });
      if (!res.ok) return null;
      const data = (await res.json().catch(() => null)) as {
        data?: {
          phase?: string;
          reasons?: string[];
          message?: {
            banner?: string | null;
            bannerTone?: "info" | "warn" | "critical" | null;
            featureDegraded?: string | null;
          };
        };
        phase?: string;
        reasons?: string[];
        message?: {
          banner?: string | null;
          bannerTone?: "info" | "warn" | "critical" | null;
          featureDegraded?: string | null;
        };
      } | null;
      if (!data) return null;
      const payload = data.data ?? data;
      if (payload) sharedLastFetchMs = Date.now();
      return payload;
    } catch {
      return null;
    } finally {
      sharedInflight = null;
      releaseSharedInfraFetch();
    }
  })();
  return sharedInflight;
}
